import { FC, useEffect, useMemo } from 'react'
import {
  useCompanyManagerDashboardAppraisalMeetingStatus,
  useCompanyManagerDashboardAppraisalSeasonYear,
} from '../../../graphql/company-manager-dashboard/hooks/useCompanyManagerDashboardAppraisalMeetingStatusQueries'
import { useDashboardStore } from '../DashboardStore'
import { computeAppraisalMonitoringCountersFromRows } from '../utils/appraisalMeetingStatusDashboardUtil'
import { MonitoringTabs } from '../dasboardStoreTypes'
import DashboardAppraisalMonitoringChartTabBody from './DashboardAppraisalMonitoringChartTabBody'
import DashboardAppraisalMonitoringHeader from './DashboardAppraisalMonitoringHeader'
import DashboardAppraisalMonitoringFooter from './DashboardAppraisalMonitoringFooter'
import DashboardAppraisalMonitoringTableTabBody from './DashboardAppraisalMonitoringTableTabBody'
import '../styles/dashboard-appraisal-monitoring.less'

const DashboardAppraisalMonitoringWidget: FC = () => {
  const [
    selectedCompanyId,
    selectedDashboardRoles,
    activeTab,
    setActiveMonitoringTab,
    filteredRows,
    allRows,
    ongoingUpdatesCount,
    setAppraisalMonitoringData,
  ] = useDashboardStore((state) => [
    state.selected.value,
    state.selectedDashboardRoles,
    state.activeMonitoringTab,
    state.setActiveMonitoringTab,
    state.appraisalMonitoringFilteredRows,
    state.appraisalMonitoringAllRows,
    state.appraisalMonitoringOngoingUpdatesCount,
    state.setAppraisalMonitoringData,
  ])

  const { data, loading, error } = useCompanyManagerDashboardAppraisalMeetingStatus(selectedCompanyId, selectedDashboardRoles)
  const {
    data: asYearData,
    loading: asYearLoading,
    error: asYearError,
  } = useCompanyManagerDashboardAppraisalSeasonYear(selectedCompanyId, selectedDashboardRoles)

  useEffect(() => {
    const payload = data?.companyManagerDashboardAppraisalMeetingStatus
    if (!payload || loading || String(payload.companyId) !== selectedCompanyId) {
      return
    }
    setAppraisalMonitoringData({
      rows: payload.rows,
      ongoingAppraisalUpdateAfterLatestConfirmedMeetingCount: payload.ongoingAppraisalUpdateAfterLatestConfirmedMeetingCount,
    })
  }, [data, loading, selectedCompanyId, setAppraisalMonitoringData])

  const counters = useMemo(() => computeAppraisalMonitoringCountersFromRows(filteredRows), [filteredRows])
  const unfilteredCounters = useMemo(() => computeAppraisalMonitoringCountersFromRows(allRows), [allRows])

  const asYear = asYearData?.companyManagerDashboardASYear ?? null
  const employeeLevels = asYearData?.employeeLevels ?? []

  return (
    <div className="dashboard-appraisal-monitoring">
      <DashboardAppraisalMonitoringHeader activeTab={activeTab} onTabChange={setActiveMonitoringTab} asYear={asYear} />

      <div className="dashboard-appraisal-monitoring__body">
        {activeTab === MonitoringTabs.CHART && (
          <DashboardAppraisalMonitoringChartTabBody
            countersLoading={loading}
            countersError={error}
            counters={counters}
            unfilteredCounters={unfilteredCounters}
          />
        )}
        {activeTab === MonitoringTabs.TABLE && (
          <DashboardAppraisalMonitoringTableTabBody
            employeeLevels={employeeLevels}
            companyId={selectedCompanyId}
            loading={loading || asYearLoading}
            error={error || asYearError}
          />
        )}
      </div>

      {ongoingUpdatesCount > 0 && <DashboardAppraisalMonitoringFooter hiddenUpdatesCount={ongoingUpdatesCount} />}
    </div>
  )
}

export default DashboardAppraisalMonitoringWidget
